import { useMutation, useQuery } from "@apollo/client/react";
import NotFound from "components/pages/notFound/NotFound";
import paths from "config/routePaths";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import { GetAllCompanies } from "hooks/apollo/company/company.gql";
import { DeleteSite, GetAssignedSites, GetSite, UpdateSite } from "hooks/apollo/site/site.gql";
import { GetAllTimeZones } from "hooks/apollo/timeZone/timeZone.gql";
import useGoBack from "hooks/useGoBack";
import Authorization from "policies/Authorization";
import React, { useState } from "react";
import SiteEditView from "./SiteEditView";

export default function SiteEditContainer({ id, companyId }) {
  const { goBack } = useGoBack();
  const policy = Authorization.usePolicy();
  const applicationContext = useApplicationContext();
  const { nulogyAdmin, nulogyUser } = applicationContext;
  const leavePage = () => {
    goBack(paths.siteIndex);
  };
  const [updateSite] = useMutation(UpdateSite, { onCompleted: () => leavePage() });
  const [removeSite] = useMutation(DeleteSite, { onCompleted: () => leavePage() });
  const { data: companiesData, loading: companiesLoading } = useQuery(GetAllCompanies);
  const { data, loading, error: queryError } = useQuery(GetSite, { variables: { id } });
  const { data: timezoneData, loading: timezonesLoading } = useQuery(GetAllTimeZones);
  const [error, setError] = useState(false);

  if (queryError?.message === "Site not found") return <NotFound />;
  if (loading || timezonesLoading || companiesLoading) return null;

  const canCreateUser = policy.canCreate(Authorization.EntityTypes.User);
  const canUpdateCompany = policy.canUpdate(Authorization.EntityTypes.Company);
  const createUserPath = paths.userNewForSite(data?.site?.company?.id, id);

  const canDelete = policy.canDelete(Authorization.EntityTypes.Site);
  const handleDelete =
    canDelete && (() => removeSite({ refetchQueries: [{ query: GetAssignedSites }], variables: { id } }));

  const handleCancel = () => leavePage();

  const handleSave = (args) => {
    const defaultSite = {
      name: "",
    };

    const site = { ...defaultSite, ...data?.site, ...args, companyId: args.company?.id };

    return updateSite({ refetchQueries: [{ query: GetAssignedSites }], variables: site });
  };

  return (
    <SiteEditView
      canCreateUser={canCreateUser}
      canUpdateCompany={canUpdateCompany}
      companies={companiesData?.companies || []}
      companyId={companyId}
      createUserPath={createUserPath}
      error={error}
      nulogyAdmin={nulogyAdmin}
      nulogyUser={nulogyUser}
      onCancel={handleCancel}
      onDelete={handleDelete}
      onSave={handleSave}
      setError={setError}
      site={data?.site}
      timeZones={timezoneData?.timeZones}
    />
  );
}
